/**
 * 平面轮廓 2D 投影与基础向量工具。
 *
 * 设计说明：
 * 1. 供 `offsetPlanarLoop` 与组合曲线偏置链路复用；
 * 2. 统一 inward/outward 与轮廓 winding 的内外法向语义；
 * 3. 保持纯函数、无 Puppy CAD 内核 依赖。
 */

import type { PlanarLoopOffsetDirection, PlanarLoopOffsetPlane } from './offsetPlanarLoop';

/** 二维点。 */
export type Point2 = [number, number];

/** 数值容差。 */
export const PLANAR_LOOP_EPSILON = 1e-8;

/**
 * 将 3D 点投影到局部 2D 平面。
 *
 * @param point 3D 点。
 * @param plane 偏置平面。
 * @returns 局部 2D 点。
 */
export function projectPointToPlane(
  point: [number, number, number],
  plane: PlanarLoopOffsetPlane,
): Point2 {
  const relativeX = point[0] - plane.origin[0];
  const relativeY = point[1] - plane.origin[1];
  const relativeZ = point[2] - plane.origin[2];
  return [
    relativeX * plane.xAxis[0] + relativeY * plane.xAxis[1] + relativeZ * plane.xAxis[2],
    relativeX * plane.yAxis[0] + relativeY * plane.yAxis[1] + relativeZ * plane.yAxis[2],
  ];
}

/**
 * 将局部 2D 点还原为 3D 点。
 *
 * @param point 2D 点。
 * @param plane 偏置平面。
 * @returns 3D 点。
 */
export function unprojectPointFromPlane(
  point: Point2,
  plane: PlanarLoopOffsetPlane,
): [number, number, number] {
  return [
    plane.origin[0] + point[0] * plane.xAxis[0] + point[1] * plane.yAxis[0],
    plane.origin[1] + point[0] * plane.xAxis[1] + point[1] * plane.yAxis[1],
    plane.origin[2] + point[0] * plane.xAxis[2] + point[1] * plane.yAxis[2],
  ];
}

/**
 * 计算二维多边形的符号面积。
 *
 * @param points 二维点集。
 * @returns 符号面积。
 */
export function computeSignedArea2(points: Point2[]): number {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    area += current[0] * next[1] - next[0] * current[1];
  }
  return area * 0.5;
}

/**
 * 归一化二维向量。
 *
 * @param vector 输入向量。
 * @returns 单位向量。
 * @throws 当向量长度小于轮廓容差、无法归一化时抛出错误。
 */
export function normalizeVector2(vector: Point2): Point2 {
  const length = Math.hypot(vector[0], vector[1]);
  if (length < PLANAR_LOOP_EPSILON) {
    throw new Error('闭合轮廓存在退化边，无法执行偏置。');
  }
  return [vector[0] / length, vector[1] / length];
}

/**
 * 计算二维向量左法向。
 *
 * @param vector 输入向量。
 * @returns 左法向。
 */
export function rotateLeft2(vector: Point2): Point2 {
  return [-vector[1], vector[0]];
}

/**
 * 计算二维向量右法向。
 *
 * @param vector 输入向量。
 * @returns 右法向。
 */
export function rotateRight2(vector: Point2): Point2 {
  return [vector[1], -vector[0]];
}

/**
 * 计算交叉积标量。
 *
 * @param left 左向量。
 * @param right 右向量。
 * @returns 叉积标量。
 */
export function cross2(left: Point2, right: Point2): number {
  return left[0] * right[1] - left[1] * right[0];
}

/**
 * 计算二维向量点积。
 *
 * @param left 左向量。
 * @param right 右向量。
 * @returns 点积结果。
 */
export function dot2(left: Point2, right: Point2): number {
  return left[0] * right[0] + left[1] * right[1];
}

/**
 * 计算指定边的“朝向内部”法向。
 *
 * @param edgeDirection 边方向。
 * @param signedArea 符号面积。
 * @returns 指向轮廓内部的法向。
 */
export function resolveInteriorNormal2(edgeDirection: Point2, signedArea: number): Point2 {
  return signedArea >= 0 ? rotateLeft2(edgeDirection) : rotateRight2(edgeDirection);
}

/**
 * 解析边偏置法向（inward 指向内部，outward 指向外部）。
 *
 * @param edgeDirection 边方向。
 * @param signedArea 符号面积。
 * @param direction 偏置方向。
 * @returns 偏置法向。
 */
export function resolveOffsetNormal2(
  edgeDirection: Point2,
  signedArea: number,
  direction: PlanarLoopOffsetDirection,
): Point2 {
  const interiorNormal = resolveInteriorNormal2(edgeDirection, signedArea);
  const scale = direction === 'inward' ? 1 : -1;
  return [interiorNormal[0] * scale, interiorNormal[1] * scale];
}

/**
 * 计算两条直线的交点。
 *
 * @param pointA 第一条线上的点。
 * @param directionA 第一条线方向。
 * @param pointB 第二条线上的点。
 * @param directionB 第二条线方向。
 * @returns 交点；平行时返回 null。
 */
export function intersectLines2(
  pointA: Point2,
  directionA: Point2,
  pointB: Point2,
  directionB: Point2,
): Point2 | null {
  const delta: Point2 = [pointB[0] - pointA[0], pointB[1] - pointA[1]];
  const denominator = cross2(directionA, directionB);
  if (Math.abs(denominator) < PLANAR_LOOP_EPSILON) {
    return null;
  }
  const factor = cross2(delta, directionB) / denominator;
  return [
    pointA[0] + directionA[0] * factor,
    pointA[1] + directionA[1] * factor,
  ];
}

/**
 * 判断两个二维点是否近似重合。
 *
 * @param left 点 A。
 * @param right 点 B。
 * @param tolerance 容差。
 * @returns 是否重合。
 */
export function arePoints2Coincident(left: Point2, right: Point2, tolerance = PLANAR_LOOP_EPSILON): boolean {
  return Math.hypot(left[0] - right[0], left[1] - right[1]) <= tolerance;
}

/**
 * 计算轮廓顶点处的偏置点（miter 交点）。
 *
 * @param previous 环上前一顶点。
 * @param current 当前顶点。
 * @param next 环上下一顶点。
 * @param signedArea 符号面积。
 * @param direction 偏置方向。
 * @param distance 偏置距离。
 * @returns 偏置后的顶点。
 */
export function offsetLoopCorner2(
  previous: Point2,
  current: Point2,
  next: Point2,
  signedArea: number,
  direction: PlanarLoopOffsetDirection,
  distance: number,
): Point2 {
  const previousDirection = normalizeVector2([
    current[0] - previous[0],
    current[1] - previous[1],
  ]);
  const nextDirection = normalizeVector2([
    next[0] - current[0],
    next[1] - current[1],
  ]);

  const previousOffsetNormal = resolveOffsetNormal2(previousDirection, signedArea, direction);
  const nextOffsetNormal = resolveOffsetNormal2(nextDirection, signedArea, direction);

  const previousPointOnLine: Point2 = [
    current[0] + previousOffsetNormal[0] * distance,
    current[1] + previousOffsetNormal[1] * distance,
  ];
  const nextPointOnLine: Point2 = [
    current[0] + nextOffsetNormal[0] * distance,
    current[1] + nextOffsetNormal[1] * distance,
  ];

  const intersection = intersectLines2(
    previousPointOnLine,
    previousDirection,
    nextPointOnLine,
    nextDirection,
  );
  if (intersection) {
    return intersection;
  }

  const averageNormal: Point2 = [
    previousOffsetNormal[0] + nextOffsetNormal[0],
    previousOffsetNormal[1] + nextOffsetNormal[1],
  ];
  const averageLength = Math.hypot(averageNormal[0], averageNormal[1]);
  if (averageLength < PLANAR_LOOP_EPSILON) {
    // 180° 折返或共线接缝：两偏移法向相反，退化为单法向平移。
    return previousPointOnLine;
  }

  const normalizedAverage = normalizeVector2(averageNormal);
  const alignment = Math.max(0.25, Math.abs(dot2(normalizedAverage, nextOffsetNormal)));

  return [
    current[0] + normalizedAverage[0] * (distance / alignment),
    current[1] + normalizedAverage[1] * (distance / alignment),
  ];
}


/**
 * 二维点加法。
 *
 * @param left 左操作数。
 * @param right 右操作数。
 * @returns 和。
 */
export function add2(left: Point2, right: Point2): Point2 {
  return [left[0] + right[0], left[1] + right[1]];
}

/**
 * 二维点减法。
 *
 * @param left 左操作数。
 * @param right 右操作数。
 * @returns 差。
 */
export function subtract2(left: Point2, right: Point2): Point2 {
  return [left[0] - right[0], left[1] - right[1]];
}

/**
 * 二维点乘标量。
 *
 * @param point 点。
 * @param scalar 标量。
 * @returns 缩放结果。
 */
export function scale2(point: Point2, scalar: number): Point2 {
  return [point[0] * scalar, point[1] * scalar];
}
